import { CommonModule } from '@angular/common';

import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, ElementRef, Inject,ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog, MatLegacyDialogConfig, MatLegacyDialogModule, MatLegacyDialogRef, MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { MatLegacyListModule } from '@angular/material/legacy-list';
import { ToastrService } from 'ngx-toastr';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppButtonModule } from '../../dave-utils-module/app-button-module/app-button.module';
import { appMatDialogDefaultConfig, isNotNullOrUndefined } from '../../helper/helper';
import * as mjsHelper from '../../helper/math-js-helper';
import { variableGroup } from '../../helper/math-js-helper';
import { HintIconComponent } from '../../hint-icon/hint-icon.component';
import { MathJsCustomScopeService } from '../../services/math-js-custom-scope.service';
import { CalculationVariableModalComponent, CalculationVariableModalDialogData, CalculationVariableModalDialogReturnData } from '../calculation-variable-modal/calculation-variable-modal.component';
import { CalculationInputResultComponent } from '../calculation-input-result/calculation-input-result.component';

export interface CalculationDialogComponentData {
    initialFormula: string;
    initialResult: number | null;
    maxDecimalPlaces: number | null;
    disabled: boolean;
    calculationContextService: MathJsCustomScopeService;
    variableSelectedCallback?: (key: string) => void;
}
export interface CalculationDialogComponentReturnData {
    result: number;
    resultRounded: number;
    formular: string;
    usedVariables: string[];
}
interface variablesVM {
    key: string;
    value: string | number;
    meta: {
        description: string;
        groupId: number;
        formula?: string;
    };
}
interface variableGroupVM {
    id: number;
    description: string;
    variables$: Observable<variablesVM[]>;
    children: variableGroupVM[];
}

function mapGroupToVm(g: variableGroup[], scope$: Observable<mjsHelper.CustomScope>) {
    return g.map((group) => {
        return {
            ...group,
            variables$: scope$.pipe(
                switchMap((s) => {
                    return s.variables$.pipe(map((v) => v.filter((v) => v.meta.groupId === group.id).sort((a, b) => (a.meta.description || '').localeCompare(b.meta.description || ''))));
                }),
            ),
            children: mapGroupToVm(group.children, scope$),
        };
    });
}

@Component({
    selector: 'app-calculation-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatLegacyInputModule,
        ReactiveFormsModule,
        MatLegacyDialogModule,
        FontAwesomeModule,
        AppButtonModule,
        CdkDrag,
        CdkDragHandle,
        HintIconComponent,
        MatLegacyButtonModule,
        MatLegacyTooltipModule,
        MatLegacyListModule,
        CalculationInputResultComponent,
    ],
    templateUrl: './calculation-dialog.component.html',
    styleUrls: ['./calculation-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculationDialogComponent {
    @ViewChild('formulaInput') formulaInput: ElementRef<HTMLInputElement>;
    public static DefaultConfig: MatLegacyDialogConfig = {
        ...appMatDialogDefaultConfig,
        hasBackdrop: false,
        autoFocus: 'first-tabbable',
        delayFocusTrap: true,
        restoreFocus: true,
        width: '50rem',
    };
    protected formulaForm: FormControl<string>;
    protected hint: string = `Mit "Anführungszeichen" können Kommentare gemacht werden

    sin(), cos(), tan() und pi können verwendet werden

    mit ^ kann potenziert werden (2³ -> 2^3)

    Eine Formel kann sich über mehrere Zeilen erstrecken, diese müssen mit einem operator (wie z.B.: +) verbunden werden`;
    protected readonly initialResultDosentMatch: boolean;
    protected readonly disabled: boolean;
    protected calculationScopeService: MathJsCustomScopeService;
    protected contextVariables: variableGroupVM;

    constructor(
        @Inject(MAT_LEGACY_DIALOG_DATA) public Data: CalculationDialogComponentData,
        private dialogRef: MatLegacyDialogRef<CalculationDialogComponent, CalculationDialogComponentReturnData>,
        private dialog: MatLegacyDialog,
        private toastr: ToastrService,
    ) {
        this.formulaForm = new FormControl<string>(Data.initialFormula || '');
        if (Data.disabled) {
            this.formulaForm.disable();
        }
        this.calculationScopeService = Data.calculationContextService;
        console.log(Data.calculationContextService?.getScope());

        this.contextVariables = {
            id: null,
            description: 'Parameter',
            variables$: this.calculationScopeService?.scope$.pipe(
                switchMap((s) => {
                    return s.variables$.pipe(map((v) => v.filter((v) => !v.meta.groupId).sort((a, b) => (a.meta.description || '').localeCompare(b.meta.description || ''))));
                }),
            ),
            children: this.calculationScopeService ? mapGroupToVm(this.calculationScopeService.getScope().groupGetAll, this.calculationScopeService.scope$) : [],
        };
        console.log({ contextVariables: this.contextVariables });
        this.disabled = Data.disabled;
        if (isNotNullOrUndefined(Data.initialResult) && Data.initialFormula) {
            this.initialResultDosentMatch = mjsHelper.evaluate(this.formulaForm.value, this.Data.maxDecimalPlaces, this.calculationScopeService ? this.calculationScopeService.getScope() : undefined).resRounded !== Data.initialResult;
        } else {
            this.initialResultDosentMatch = false;
        }
    }
    close() {
        const r = mjsHelper.evaluate(this.formulaForm.value, this.Data.maxDecimalPlaces, this.calculationScopeService ? this.calculationScopeService.getScope() : undefined);
        this.dialogRef.close({ result: r.invalid ? null : r.res, resultRounded: r.invalid ? null : r.resRounded, formular: this.formulaForm.value, usedVariables: mjsHelper.getUsedVariableNames(this.formulaForm.value) });
    }

    protected onAddClick(groupId: number | null) {
        this.dialog
            .open<CalculationVariableModalComponent, CalculationVariableModalDialogData, CalculationVariableModalDialogReturnData>(CalculationVariableModalComponent, {
                ...CalculationVariableModalComponent.DefaultConfig,
                data: {
                    initialValues: null,
                    mathScope: this.calculationScopeService.getScope(),
                    maxDecimalPlaces: this.Data.maxDecimalPlaces,
                },
            })
            .afterClosed()
            .subscribe((r) => {
                if (r) {
                    this.calculationScopeService.getScope().setAsFormula(r.key, r.formula, { description: r.description, groupId });
                    // this.calculationScopeService.getScope().setMeta(r.key, { description: r.description, formula: r.formula, groupId });
                }
            });
    }

    protected onEditClick(v: variablesVM) {
        let formula = v.meta.formula;
        if (!formula) {
            formula = isNotNullOrUndefined(v.value) ? v.value.toString() : '';
        }
        this.dialog
            .open<CalculationVariableModalComponent, CalculationVariableModalDialogData, CalculationVariableModalDialogReturnData>(CalculationVariableModalComponent, {
                ...CalculationVariableModalComponent.DefaultConfig,
                data: {
                    initialValues: {
                        description: v.meta.description,
                        formula,
                        key: v.key,
                    },
                    mathScope: this.calculationScopeService.getScope(),
                    maxDecimalPlaces: this.Data.maxDecimalPlaces,
                },
            })
            .afterClosed()
            .subscribe((r) => {
                if (r) {
                    if (r.deleted || v.key !== r.key) {
                        this.calculationScopeService.getScope().removeVariable(v.key);
                    }
                    if (!r.deleted) {
                        this.calculationScopeService.getScope().setAsFormula(r.key, r.formula, { description: r.description, groupId: v.meta.groupId });
                        // this.calculationScopeService.getScope().setMeta(r.key, { description: r.description, formula: r.formula, groupId: v.meta.groupId });
                    }
                }
            });
    }

    protected onSelectVariable(v: variablesVM) {
        if (this.formulaInput?.nativeElement && this.formulaInput.nativeElement === document.activeElement) {
            const index = this.formulaInput.nativeElement.selectionStart;
            const indexEnd = this.formulaInput.nativeElement.selectionEnd;
            if (isNotNullOrUndefined(index)) {
                this.formulaForm.setValue(this.formulaForm.value.substring(0, index) + v.key + this.formulaForm.value.substring(indexEnd));
                this.formulaInput.nativeElement.setSelectionRange(index + v.key.length, index + v.key.length);
                return;
            }
        }
        this.formulaForm.setValue(this.formulaForm.value + v.key);
    }
}
